import React, { useContext, useMemo } from "react";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import "./sidenav.scss";
import {
  ANALYTICS_HUB_ITEM,
  PROPERTY_CONFIG_ITEM,
  CONTACT_US_ITEM,
  USER_ACTIVITY_ITEM,
  getHelpCenterURl,
} from "./utils";
import NavItem from "./NavItem";
import NavItemWithSubMenu from "./NavItemWithSubMenu";
import { AnalyticsHubContext } from "../../contexts/AnalyticsHubContext";
import { Can, P, menuFeatures } from "../../rbac";
import { isInternalUser } from "../../constants/utils";

import logoIcon from "../../assets/img/ht_logo.svg";
import logoWordmark from "../../assets/img/ht_logo_wordmark.svg";
import BulbIcon from "../../assets/img/light_bulb.svg?react";

const SideNav = ({ isOpen, isOffline = false, recommendationCount = 0 }) => {
  const { pages, isLoading } = useContext(AnalyticsHubContext);

  const collapsed = !isOpen;
  const helpUrl = getHelpCenterURl?.(isOffline) ?? "#";

  // Generate dynamic sub-menu items from PowerBI pages
  const analyticsHubItem = useMemo(() => {
    // If pages are loaded from PowerBI, use them; otherwise use default from utils
    const subMenuItems =
      !isLoading && pages.length > 0
        ? pages.map((page) => {
            // Use displayName for URL (convert to kebab-case)
            const urlSlug = page.displayName
              .toLowerCase()
              .replaceAll(/\s+/g, "-");
            return {
              key: urlSlug,
              label: page.displayName,
              to: `/analytics-hub/${urlSlug}`,
            };
          })
        : ANALYTICS_HUB_ITEM.subMenuItems;

    return {
      ...ANALYTICS_HUB_ITEM,
      subMenuItems,
    };
  }, [pages, isLoading]);

  return (
    <aside className={["sidenav", isOpen ? "open" : ""].join(" ")}>
      {/* Brand */}
      <div className="sidenav__brand">
        <NavLink
          to="/"
          className="sidenav__brand__link"
          aria-label="Go to home"
        >
          {collapsed ? (
            <img
              src={logoIcon}
              className="sidenav__brand__logo"
              alt="HotelTrader"
            />
          ) : (
            <img
              src={logoWordmark}
              className="sidenav__brand__logo--wordmark"
              alt="HotelTrader"
            />
          )}
        </NavLink>
      </div>
      {/* Main menu */}
      <nav className="sidenav__menu" aria-label="Primary">
        {menuFeatures.map((feature) => (
          <Can key={feature.id} permission={feature.permission}>
            <NavItem
              to={feature.route}
              label={feature.label}
              Icon={feature.icon}
              collapsed={collapsed}
              badge={feature.id === "home" ? recommendationCount : null}
            />
          </Can>
        ))}

        {/* Analytics Hub with sub-navigation */}
        <Can permission={P.AnalyticsHub.All}>
          <NavItemWithSubMenu
            key={analyticsHubItem.key}
            label={analyticsHubItem.label}
            Icon={analyticsHubItem.icon}
            collapsed={collapsed}
            subMenuItems={analyticsHubItem.subMenuItems}
            onItemClick={analyticsHubItem.onItemClick}
          />
        </Can>

        {/* Property Profile with sub-navigation */}
        <Can permission={P.PropertyConfig.View}>
          <NavItemWithSubMenu
            key={PROPERTY_CONFIG_ITEM.key}
            label={PROPERTY_CONFIG_ITEM.label}
            Icon={PROPERTY_CONFIG_ITEM.icon}
            collapsed={collapsed}
            subMenuItems={PROPERTY_CONFIG_ITEM.subMenuItems}
          />
        </Can>

        {/* User Activity — internal users only; the route mirrors this in RequireInternalUser.
            The permission narrows it further. */}
        {isInternalUser() && (
          <Can permission={P.UserActivity.View}>
            <NavItem
              key={USER_ACTIVITY_ITEM.key}
              to={USER_ACTIVITY_ITEM.to}
              label={USER_ACTIVITY_ITEM.label}
              Icon={USER_ACTIVITY_ITEM.icon}
              collapsed={collapsed}
            />
          </Can>
        )}
      </nav>
      {/* Contact Us and Help Center at the bottom */}
      <div className="sidenav__bottom">
        <NavItem
          key={CONTACT_US_ITEM.key}
          to={CONTACT_US_ITEM.to}
          label={CONTACT_US_ITEM.label}
          Icon={CONTACT_US_ITEM.icon}
          collapsed={collapsed}
        />
        <NavItem
          key="Knowledge Center"
          to={helpUrl}
          label="Knowledge Center"
          Icon={BulbIcon}
          collapsed={collapsed}
          target="_blank"
        />
      </div>
    </aside>
  );
};

SideNav.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isOffline: PropTypes.bool,
  recommendationCount: PropTypes.number,
};

export default React.memo(SideNav);
